import { useCallback, useEffect, useRef, useState } from "react";
import { CompareContentItem } from "../types";

export interface UseItemPreviewResult {
  previewItem: CompareContentItem | undefined;
  startPreview: (item: CompareContentItem) => void;
  endPreview: () => void;
}

/**
 * In-modal preview state. While previewing, Escape returns to the comparison
 * instead of closing the modal, and focus goes back to whatever started the
 * preview (the comparison stays mounted underneath, so it is still there).
 */
export const useItemPreview = (): UseItemPreviewResult => {
  const [previewItem, setPreviewItem] = useState<CompareContentItem>();
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const startPreview = useCallback((item: CompareContentItem) => {
    returnFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    setPreviewItem(item);
  }, []);

  const endPreview = useCallback(() => {
    setPreviewItem(undefined);
    const target = returnFocusRef.current;
    returnFocusRef.current = null;
    // Wait for the comparison to become visible again before focusing it.
    requestAnimationFrame(() => target?.isConnected && target.focus());
  }, []);

  useEffect(() => {
    if (!previewItem) {
      return undefined;
    }
    // Capture phase on window runs before the modal's own Escape listener on
    // document, so stopping here keeps the modal open.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        event.preventDefault();
        endPreview();
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [previewItem, endPreview]);

  return { previewItem, startPreview, endPreview };
};
